<template>
  <div class="mcontainer">
    <!-- Header -->
    <div class="header-bar">
      <h2>My Business Opportunities</h2>
      <div class="actions">
        <div class="search">
          <Icon icon="mdi:magnify" width="18" height="18" class="search-icon" />
          <input
            class="search-input"
            v-model="search"
            @input="debouncedFetch"
            placeholder="Search my submissions"
          />
        </div>
      </div>
    </div>

    <!-- Card -->
    <div class="card">
      <div class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th style="width:120px">Gambar</th>
              <th>Nama</th>
              <th>Deskripsi</th>
              <th>Status</th>
              <th style="width:160px">Dibuat</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="it in list" :key="it.id">
              <td>
                <img v-if="it.image" :src="it.image" class="thumb" alt="" />
                <div v-else class="thumb placeholder">
                  <Icon icon="mdi:image-off-outline" width="22" height="22" />
                </div>
              </td>
              <td class="name-cell">
                <div class="name">{{ it.name }}</div>
              </td>
              <td class="desc-cell">
                <div class="muted">{{ truncate(it.description, 160) }}</div>
              </td>
              <td>
                <span :class="['badge', it.status]">{{ it.status }}</span>
              </td>
              <td class="muted">
                {{ formatDate(it.created_at) }}
              </td>
            </tr>

            <tr v-if="!loading && list.length === 0">
              <td colspan="5" class="empty-state">
                <Icon icon="mdi:clipboard-text-off-outline" width="22" height="22" />
                <span>Belum ada submission</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="loading-wrap" v-if="loading">
        <b-spinner small />
      </div>

      <!-- Pagination -->
      <div v-if="totalPage > 1" class="pager-wrap">
        <div class="pager-info">Page {{ page }} of {{ totalPage }}</div>
        <div class="pager-controls">
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="First">
            <Icon icon="mdi:chevron-double-left" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Prev">
            <Icon icon="mdi:chevron-left" width="18" height="18" />
          </button>

          <button
            v-for="p in pages"
            :key="p"
            class="num-btn"
            :class="{ active: p === page }"
            :disabled="p === page || loading"
            @click="go(p)"
          >
            {{ p }}
          </button>

          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Next">
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Last">
            <Icon icon="mdi:chevron-double-right" width="18" height="18" />
          </button>
        </div>
      </div>
      <!-- End Pagination -->
    </div>

    <!-- Info box -->
    <div class="hint">
      <Icon icon="mdi:information-outline" width="18" height="18" />
      <span>Submission berstatus pending akan direview oleh admin. Setelah approved akan tampil di halaman Business Opportunity publik.</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'BusinessOpportunityMine',
  components: { Icon },
  data() {
    return {
      list: [],
      loading: false,
      page: 1,
      totalPage: 1,
      perPage: 10,
      search: '',
      debouncedFetch: null
    }
  },
  created() {
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed: {
    pages() {
      const t = this.totalPage
      const cur = this.page
      const win = 5
      const arr = []
      let start = Math.max(1, cur - 2)
      let end = Math.min(t, start + win - 1)
      if (end - start < win - 1) start = Math.max(1, end - win + 1)
      for (let i = start; i <= end; i++) arr.push(i)
      return arr
    }
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.set('page', this.page)
        params.set('per_page', this.perPage)
        if (this.search) params.set('search', this.search)

        const { data } = await axios.get(`/bo/mine?${params.toString()}`)
        this.list = data?.data || []
        this.page = data?.current_page || 1
        this.totalPage = data?.last_page || 1
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    go(p) {
      if (p < 1 || p > this.totalPage || p === this.page) return
      this.page = p
      this.fetchList()
    },
    truncate(s, n) {
      return s && s.length > n ? s.slice(0, n) + '...' : s || ''
    },
    formatDate(dt) {
      if (!dt) return '-'
      try {
        const d = new Date(dt)
        return d.toLocaleString()
      } catch (e) {
        return dt
      }
    },
    debounce(fn, d) {
      let t = null
      return (...a) => {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, a), d)
      }
    }
  }
}
</script>

<style scoped>
.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.actions { display: flex; align-items: center; gap: 10px; }

/* Search */
.search { position: relative; min-width: 260px; width: 320px; }
.search-icon { position: absolute; top: 10px; left: 12px; color: #9ca3af; }
.search-input {
  width: 100%; height: 38px; border: 1px solid #e5e7eb; border-radius: 10px;
  padding: 0 12px 0 36px; background: #fff; font-size: 14px;
}

/* Card and table */
.card { background: #fff; border: 1px solid #eef2f7; border-radius: 14px; padding: 16px; }
.table-wrapper { overflow-x: auto; }
.table { width: 100%; border-collapse: separate; border-spacing: 0; }
.table thead th {
  background: #fafafa; border-bottom: 1px solid #eef2f7; text-align: left; padding: 10px;
  font-weight: 600; color: #6b7280; font-size: 13px;
}
.table tbody td { border-bottom: 1px solid #f3f4f6; padding: 10px; vertical-align: top; font-size: 14px; }
.name-cell .name { font-weight: 600; }
.desc-cell .muted { color: #6b7280; }

/* Thumbnail */
.thumb { width: 100px; height: 70px; object-fit: cover; border-radius: 8px; border: 1px solid #eee; }
.thumb.placeholder {
  display: flex; align-items: center; justify-content: center; background: #fafafa; color: #9ca3af;
}

/* Badges */
.badge { display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 12px; text-transform: capitalize; border: 1px solid transparent; }
.badge.pending { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
.badge.approved { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.badge.rejected { background: #fef2f2; color: #991b1b; border-color: #fecaca; }

/* Empty and loader */
.empty-state { text-align: center; color: #9ca3af; height: 80px; }
.loading-wrap { display: flex; justify-content: center; padding: 10px 0; }

/* Pagination */
.pager-wrap { display: flex; align-items: center; justify-content: space-between; margin-top: 12px; }
.pager-info { font-size: 13px; color: #6b7280; }
.pager-controls { display: inline-flex; align-items: center; gap: 8px; }

.icon-btn {
  height: 36px; width: 36px; border: 1px solid #e5e7eb; background: #fff; border-radius: 10px;
}
.num-btn {
  min-width: 36px; height: 36px; border: 1px solid #e5e7eb; background: #fff; border-radius: 10px;
}
.num-btn.active { background: #2563eb; border-color: #2563eb; color: #fff; }

/* Hint */
.hint {
  display: flex; gap: 8px; align-items: center; margin-top: 12px;
  color: #6b7280; font-size: 13px;
}

/* Responsive */
@media (max-width: 768px) {
  .actions { width: 100%; }
  .search { width: 100%; min-width: 0; }
  .header-bar { flex-direction: column; align-items: stretch; gap: 8px; }
}
</style>